import { useEffect, useRef, useState } from 'react';

export default function AboutSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="about" className="relative py-28 lg:py-36 bg-neutral-50 overflow-hidden">
      {/* Background Text */}
      <div className="absolute top-8 left-0 pointer-events-none select-none overflow-hidden">
        <span className="block font-heading text-[10rem] lg:text-[18rem] leading-none text-black/[0.02] tracking-wider">
          ABOUT
        </span>
      </div>

      <div className="relative max-w-[1400px] mx-auto px-6 lg:px-16">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Left: Image */}
          <div className={`relative transition-all duration-700 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 -translate-x-8'}`}>
            <div className="relative aspect-[4/5] bg-black overflow-hidden">
              <img
                src="https://public.readdy.ai/ai/img_res/edited_594e0c1fc0b1601d3f9f8ecdd719d924_bf9fc3c2.jpg"
                alt="專業鑑定團隊"
                className="w-full h-full object-cover object-center"
              />
              {/* Overlay Badge */}
              <div className="absolute bottom-8 left-8 right-8 bg-white/95 backdrop-blur-sm p-6 border border-black/10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-black flex items-center justify-center flex-shrink-0">
                    <i className="ri-shield-check-line text-2xl text-white"></i>
                  </div>
                  <div>
                    <div className="font-heading text-2xl text-black">10+ 年</div>
                    <div className="font-body text-[11px] text-neutral-600 tracking-wider">專業鑑定經驗</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right: Content */}
          <div className={`transition-all duration-700 delay-300 ${visible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="flex items-center gap-3 mb-6">
              <div className="w-10 h-px bg-black"></div>
              <span className="text-[11px] font-body font-medium tracking-[0.25em] text-neutral-500 uppercase">
                About Tricore
              </span>
            </div>

            <h2 className="font-heading text-5xl lg:text-6xl text-black leading-[1] tracking-wide mb-8">
              科學鑑定<br />客觀憑證
            </h2>

            <div className="space-y-6 mb-10">
              <p className="font-body text-[15px] text-neutral-700 leading-[1.8]">
                三鑑所 Tricore 專精於<strong className="text-black">名牌包、翡翠、寶石</strong>的科學鑑定服務。我們結合 <strong className="text-black">Entrupy AI 儀器驗真</strong>、<strong className="text-black">Legit App 技術</strong>與<strong className="text-black">中檢標準</strong>,為您的珍貴資產提供客觀憑證。
              </p>
              <p className="font-body text-[15px] text-neutral-700 leading-[1.8]">
                我們堅持「<strong className="text-black">講憑據,不講故事</strong>」的理念,拒絕模糊定價與主觀判斷。每一件送檢物品都經過嚴格的科學檢測流程,確保您獲得最可靠的鑑定結果。
              </p>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-3 gap-6 mb-10">
              <div className="border-l-2 border-black pl-4">
                <div className="font-heading text-3xl text-black mb-1">5000+</div>
                <div className="font-body text-[11px] text-neutral-600 tracking-wider">鑑定案例</div>
              </div>
              <div className="border-l-2 border-black pl-4">
                <div className="font-heading text-3xl text-black mb-1">98%</div>
                <div className="font-body text-[11px] text-neutral-600 tracking-wider">客戶滿意度</div>
              </div>
              <div className="border-l-2 border-black pl-4">
                <div className="font-heading text-3xl text-black mb-1">100%</div>
                <div className="font-body text-[11px] text-neutral-600 tracking-wider">科學驗證</div>
              </div>
            </div>

            {/* CTA */}
            <a
              href="#appointment"
              className="inline-flex items-center gap-3 px-8 py-4 bg-black text-white font-body text-[14px] font-semibold tracking-wider hover:bg-neutral-800 transition-all duration-300 cursor-pointer whitespace-nowrap group"
            >
              預約鑑定服務
              <i className="ri-arrow-right-line text-base group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
